import { useMutation, useQueryClient } from '@tanstack/react-query';
import clientApi, { IClientApiResponse } from '~/clientApi';
import { Creator } from '~/clientApi/creator.model';
import {
  AGENCY_SAVED_LISTS,
  SAVED_LISTS,
  SAVED_LIST_CREATORS,
} from '~/queries/keys';
import { ICreator } from '~/types';

export interface IUseUpdateSaveListCreatorsParams {
  addCreatorIds?: string[];
  id: string;
  removeCreatorIds?: string[];
}

export interface UseUpdateSaveListCreatorsParams {
  disableInvalidation?: boolean;
  queryKey?: string[];
}

export const useUpdateSaveListCreators = ({
  disableInvalidation = false,
  queryKey,
}: UseUpdateSaveListCreatorsParams = {}) => {
  const queryClient = useQueryClient();

  const mutation = useMutation({
    mutationFn: async ({
      addCreatorIds,
      id,
      removeCreatorIds,
    }: IUseUpdateSaveListCreatorsParams) => {
      let promises: Promise<IClientApiResponse<Creator>>[] = [];
      if (addCreatorIds?.length) {
        promises.push(
          clientApi.savedLists.addCreators({
            creatorIds: addCreatorIds,
            id,
            _isImpersonatingDisabled: true,
          }),
        );
      }
      if (removeCreatorIds?.length) {
        promises.push(
          clientApi.savedLists.removeCreators({
            creatorIds: removeCreatorIds,
            id,
            _isImpersonatingDisabled: true,
          }),
        );
      }
      return Promise.all(promises).then((data) => data.pop());
    },
    onMutate: async ({ id, removeCreatorIds }) => {
      await queryClient.cancelQueries({
        queryKey: queryKey || [SAVED_LIST_CREATORS, id],
      });

      const previousCreators = queryClient.getQueryData(
        queryKey || [SAVED_LIST_CREATORS, id],
      );

      queryClient.setQueryData(
        queryKey || [SAVED_LIST_CREATORS, id],
        (old: any) => ({
          ...old,
          pages:
            old?.pages?.map((page: any) => {
              const updatedResults = page?.results?.filter(
                (creator: ICreator) => !removeCreatorIds?.includes(creator.id),
              );
              return { ...page, results: updatedResults };
            }) || [],
        }),
      );

      return { previousCreators };
    },
    onSuccess: (_data) => {
      if (disableInvalidation) return;

      queryClient.invalidateQueries([SAVED_LIST_CREATORS]);
      queryClient.invalidateQueries({
        queryKey: [SAVED_LISTS],
      });
      queryClient.invalidateQueries({
        queryKey: [AGENCY_SAVED_LISTS],
      });
    },
    onError: (_err, variables, context) => {
      queryClient.setQueryData(
        queryKey || [SAVED_LIST_CREATORS, variables.id],
        context?.previousCreators,
      );
    },
  });

  return mutation;
};
